<template>
  <!-- Top-level editor gets the same centred page spacing as the rendered
       homepage (.v3-pagewrap); nested group editors stay full-width. -->
  <div class="v3e v3-widget" :class="{ 'v3-pagewrap': !nested }">
    <draggable
      :list="rows"
      handle=".v3e-row-grip"
      :group="gid + '-rows'"
      tag="div"
      class="v3e-rows"
      ghost-class="v3e-ghost"
    >
      <div v-for="(row, ri) in rows" :key="'row' + ri" class="v3e-row">
        <div class="v3e-row-head">
          <span class="v3e-grip v3e-row-grip" title="Drag to reorder row">
            <v-icon small>drag_indicator</v-icon>
          </span>
          <span class="v3e-row-title">ROW {{ ri + 1 }}</span>
          <span class="v3e-row-sub">{{ row.cols.length }} column{{ row.cols.length === 1 ? '' : 's' }}</span>
          <span class="v3e-grow"></span>
          <button class="v3e-icon" title="Delete row" @click="$emit('delete-row', row)">
            <v-icon small>delete</v-icon>
          </button>
        </div>

        <div class="v3e-row-body">
        <draggable
          :list="row.cols"
          handle=".v3e-col-grip"
          :group="gid + '-cols-' + ri"
          tag="div"
          class="v3e-cols"
          ghost-class="v3e-ghost"
        >
          <div
            v-for="(col, ci) in row.cols"
            :key="'col' + ci"
            class="v3e-col"
            :style="{ flex: (col.cols || 1) + ' 1 0' }"
          >
            <div class="v3e-col-head">
              <span class="v3e-grip v3e-col-grip" title="Drag to reorder column">
                <v-icon x-small>drag_indicator</v-icon>
              </span>
              <span class="v3e-w">WIDTH</span>
              <input
                type="number"
                min="1"
                max="12"
                class="v3e-span"
                :value="col.cols"
                @change="(e) => $emit('set-span', { col, value: +e.target.value })"
              />
              <span class="v3e-w">/ 12</span>
              <span class="v3e-grow"></span>
              <button
                class="v3e-icon"
                title="Delete column"
                :disabled="row.cols.length <= 1"
                @click="$emit('delete-column', { row, col })"
              >
                <v-icon x-small>delete</v-icon>
              </button>
            </div>

            <draggable
              :list="col.rows"
              handle=".v3e-ctrl-grip"
              :group="gid + '-ctrls'"
              tag="div"
              class="v3e-ctrls"
              ghost-class="v3e-ghost"
            >
              <div v-for="(ctrl, ki) in col.rows" :key="'ctrl' + ki" class="v3e-ctrl">
                <div class="v3e-ctrl-bar">
                  <span class="v3e-grip v3e-ctrl-grip" title="Drag to reorder / move control">
                    <v-icon x-small>drag_indicator</v-icon>
                  </span>
                  <span class="v3e-ctrl-name">{{ controlLabel(ctrl.controlType) }}</span>
                  <span class="v3e-grow"></span>
                  <button class="v3e-icon" title="Configure" @click="$emit('edit-control', ctrl)">
                    <v-icon x-small>settings</v-icon>
                  </button>
                  <button class="v3e-icon" title="Remove" @click="$emit('delete-control', { col, ctrl })">
                    <v-icon x-small>delete</v-icon>
                  </button>
                </div>
                <div class="v3e-ctrl-body">
                  <LandingHero v-if="ctrl.controlType === 'v3Hero'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingStatRow v-else-if="ctrl.controlType === 'v3StatRow'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3QuickLinks v-else-if="ctrl.controlType === 'v3QuickLinks'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3DonutChart v-else-if="ctrl.controlType === 'v3DonutChart'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3BarChart v-else-if="ctrl.controlType === 'v3BarChart'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3FeedbackStrip v-else-if="ctrl.controlType === 'v3FeedbackStrip'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3TaskList v-else-if="ctrl.controlType === 'v3TaskList'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3TaskTable v-else-if="ctrl.controlType === 'v3TaskTable'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3Delegated v-else-if="ctrl.controlType === 'v3Delegated'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3Empty v-else-if="ctrl.controlType === 'v3Empty'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3AdminPriority v-else-if="ctrl.controlType === 'v3AdminPriority'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3AttentionQueue v-else-if="ctrl.controlType === 'v3AttentionQueue'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3Activity v-else-if="ctrl.controlType === 'v3Activity'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3PriorityStack v-else-if="ctrl.controlType === 'v3PriorityStack'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3ScoreRing v-else-if="ctrl.controlType === 'v3ScoreRing'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3LinkList v-else-if="ctrl.controlType === 'v3LinkList'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3FeatureTiles v-else-if="ctrl.controlType === 'v3FeatureTiles'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3DocList v-else-if="ctrl.controlType === 'v3DocList'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3DocListFull v-else-if="ctrl.controlType === 'v3DocListFull'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3ActionPanel v-else-if="ctrl.controlType === 'v3ActionPanel'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3Stepper v-else-if="ctrl.controlType === 'v3Stepper'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3CoachToast v-else-if="ctrl.controlType === 'v3CoachToast'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3Calibration v-else-if="ctrl.controlType === 'v3Calibration'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3Audit v-else-if="ctrl.controlType === 'v3Audit'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <LandingV3Insights v-else-if="ctrl.controlType === 'v3Insights'" :definition="ctrl" :editMode="true" :qaActions="qaActions" />
                  <!-- Nested group → recurse into another editor. -->
                  <cLandingV3Editor
                    v-else-if="ctrl.controlType === 'v3Group'"
                    :rows="[ctrl]"
                    :nested="true"
                    :qaActions="qaActions"
                    :controlTypes="controlTypes"
                    @add-row="$emit('add-row')"
                    @delete-row="(r) => $emit('delete-row', r)"
                    @add-column="(r) => $emit('add-column', r)"
                    @delete-column="(p) => $emit('delete-column', p)"
                    @set-span="(p) => $emit('set-span', p)"
                    @add-control="(c) => $emit('add-control', c)"
                    @edit-control="(c) => $emit('edit-control', c)"
                    @delete-control="(p) => $emit('delete-control', p)"
                  />
                  <div v-else class="v3e-unknown">{{ controlLabel(ctrl.controlType) }}</div>
                </div>
              </div>
            </draggable>

            <button class="v3e-add" @click="$emit('add-control', col)">
              <v-icon small>add</v-icon> Add Control
            </button>
          </div>
        </draggable>

        <button class="v3e-addcol" title="Add a column" @click="$emit('add-column', row)">
          <v-icon small>add</v-icon>
        </button>
        </div>
      </div>
    </draggable>

    <button class="v3e-add v3e-add-row" @click="$emit('add-row')">
      <v-icon>add</v-icon> Add row
    </button>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import LandingHero from "./cLandingHero";
import LandingStatRow from "./cLandingStatRow";
import LandingV3QuickLinks from "./cLandingV3QuickLinks";
import LandingV3DonutChart from "./cLandingV3DonutChart";
import LandingV3BarChart from "./cLandingV3BarChart";
import LandingV3FeedbackStrip from "./cLandingV3FeedbackStrip";
import LandingV3TaskList from "./cLandingV3TaskList";
import LandingV3TaskTable from "./cLandingV3TaskTable";
import LandingV3Delegated from "./cLandingV3Delegated";
import LandingV3Empty from "./cLandingV3Empty";
import LandingV3AdminPriority from "./cLandingV3AdminPriority";
import LandingV3AttentionQueue from "./cLandingV3AttentionQueue";
import LandingV3Activity from "./cLandingV3Activity";
import LandingV3PriorityStack from "./cLandingV3PriorityStack";
import LandingV3ScoreRing from "./cLandingV3ScoreRing";
import LandingV3LinkList from "./cLandingV3LinkList";
import LandingV3FeatureTiles from "./cLandingV3FeatureTiles";
import LandingV3DocList from "./cLandingV3DocList";
import LandingV3DocListFull from "./cLandingV3DocListFull";
import LandingV3ActionPanel from "./cLandingV3ActionPanel";
import LandingV3Stepper from "./cLandingV3Stepper";
import LandingV3CoachToast from "./cLandingV3CoachToast";
import LandingV3Calibration from "./cLandingV3Calibration";
import LandingV3Audit from "./cLandingV3Audit";
import LandingV3Insights from "./cLandingV3Insights";

// Visual editor for the new (v3) engine — rows → columns → controls, matching
// the design's nested layout. CRUD is event-driven (vLandingConfig owns the
// definition mutation + control-settings dialog). Drag-reordering uses
// vuedraggable's `:list` so it mutates the definition arrays in place:
//   · rows reorder within the page
//   · columns reorder within their row
//   · controls reorder within a column AND move between any columns (any row)
// A v3Group control nests by recursing into itself. Group names are namespaced
// per editor instance (`gid`) so a nested editor's drags can't collide with the
// parent's.
export default {
  name: "cLandingV3Editor",
  components: { draggable, LandingHero, LandingStatRow, LandingV3QuickLinks, LandingV3DonutChart, LandingV3BarChart, LandingV3FeedbackStrip, LandingV3TaskList, LandingV3TaskTable, LandingV3Delegated, LandingV3Empty, LandingV3AdminPriority, LandingV3AttentionQueue, LandingV3Activity, LandingV3PriorityStack, LandingV3ScoreRing, LandingV3LinkList, LandingV3FeatureTiles, LandingV3DocList, LandingV3DocListFull, LandingV3ActionPanel, LandingV3Stepper, LandingV3CoachToast, LandingV3Calibration, LandingV3Audit, LandingV3Insights },
  props: {
    rows: { type: Array, default: () => [] },
    qaActions: { type: Array, default: () => [] },
    controlTypes: { type: Array, default: () => [] },
    nested: { type: Boolean, default: false },
  },
  computed: {
    gid() {
      return "v3e" + this._uid;
    },
  },
  methods: {
    controlLabel(type) {
      const m = (this.controlTypes || []).find((c) => c.value === type);
      return (m && m.text) || type || "Control";
    },
  },
};
</script>

<!-- Editor chrome aligned to the Claude Design prototype's edit mode (.v3-erow /
     .v3-ecol / .v3-addrow): bar+body rows, dashed accent-tinted column cards,
     floating per-control tool pills, full-width accent add buttons. The root
     carries `.v3-widget`, so the --v3-* tokens resolve here. -->
<style scoped lang="scss">
.v3e { display: flex; flex-direction: column; gap: 14px; }
.v3e-rows { display: flex; flex-direction: column; gap: 14px; }
/* Row = bar + body (no heavy card). */
.v3e-row { position: relative; display: flex; flex-direction: column; gap: 7px; }
.v3e-row-head { display: flex; align-items: center; gap: 8px; padding: 0 2px; }
.v3e-row-title { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--v3-text-2); }
.v3e-row-sub { font-size: 11px; color: var(--v3-text-3); }
.v3e-grow { flex: 1 1 auto; }
.v3e-grip { display: inline-flex; cursor: grab; color: var(--v3-text-3); }
.v3e-grip:hover { color: var(--v3-text-2); }
.v3e-row-body { display: flex; gap: 10px; align-items: stretch; }
.v3e-cols { flex: 1 1 auto; min-width: 0; display: flex; gap: 10px; align-items: stretch; }
.v3e-addcol {
  flex: 0 0 46px;
  align-self: stretch;
  min-height: 96px;
  border: 1.5px dashed var(--v3-border);
  border-radius: var(--v3-r-lg, 14px);
  background: color-mix(in srgb, var(--v3-accent) 2%, transparent);
  color: var(--v3-text-3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 120ms, color 120ms, background 120ms;
}
.v3e-addcol:hover { border-color: var(--v3-accent); color: var(--v3-accent); background: color-mix(in srgb, var(--v3-accent) 7%, transparent); }
/* Column = dashed accent card. */
.v3e-col {
  min-width: 0;
  border: 1.5px dashed color-mix(in srgb, var(--v3-accent) 40%, transparent);
  border-radius: var(--v3-r-lg, 14px);
  background: color-mix(in srgb, var(--v3-accent) 3%, transparent);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.v3e-col-head { display: flex; align-items: center; gap: 6px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--v3-text-3); }
.v3e-w { white-space: nowrap; }
.v3e-span { width: 38px; border: 1px solid var(--v3-border); border-radius: 6px; padding: 1px 4px; font-size: 12px; text-align: center; background: var(--v3-surface); color: var(--v3-text); }
.v3e-ctrls { display: flex; flex-direction: column; gap: 10px; min-height: 8px; }
/* Control = the real widget with a floating tools pill (no boxing card). */
.v3e-ctrl { position: relative; border-radius: var(--v3-r-md, 12px); }
.v3e-ctrl-bar {
  position: absolute; top: 6px; right: 6px; z-index: 5;
  display: flex; align-items: center; gap: 1px;
  background: var(--v3-surface); border: 1px solid var(--v3-border); border-radius: 999px;
  padding: 3px 6px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.06);
}
.v3e-ctrl-name { font-size: 11px; font-weight: 650; color: var(--v3-text-2); padding: 0 3px; }
.v3e-ctrl-body { border-radius: inherit; }
.v3e-ghost { opacity: 0.45; }
.v3e-icon {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--v3-text-3);
  border-radius: 999px;
  padding: 3px;
  display: inline-flex;
}
.v3e-icon:hover { background: rgba(16, 24, 40, 0.07); color: var(--v3-text); }
.v3e-icon:disabled { opacity: 0.3; cursor: default; }
.v3e-add {
  width: 100%;
  box-sizing: border-box;
  border: 1px dashed var(--v3-border);
  background: transparent;
  border-radius: var(--v3-r-sm, 8px);
  padding: 9px 12px;
  color: var(--v3-accent);
  font-size: 12.5px;
  font-weight: 650;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  transition: border-color 120ms, color 120ms, background 120ms;
}
.v3e-add:hover { background: color-mix(in srgb, var(--v3-accent) 6%, transparent); border-color: var(--v3-accent); }
.v3e-add-row {
  min-height: 50px;
  border: 1.5px dashed var(--v3-border);
  border-radius: var(--v3-r-lg, 14px);
  background: color-mix(in srgb, var(--v3-accent) 2%, transparent);
  color: var(--v3-text-3);
}
.v3e-add-row:hover { border-color: var(--v3-accent); color: var(--v3-accent); background: color-mix(in srgb, var(--v3-accent) 7%, transparent); }
.v3e-unknown { font-size: 12px; color: var(--v3-text-3); padding: 12px; text-align: center; border: 1px dashed var(--v3-border); border-radius: var(--v3-r-md, 12px); }
</style>
